import React, { useEffect, useMemo, useRef, useState } from "react";
import { parseDate } from "@/lib/dates";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { resolveInfoBlocks } from "@/lib/infoBlocks";
import { DEFAULT_HOTEL_PERKS, HOTEL_PERKS_TITLE } from "@/lib/hotelPerks";
import { loadTracking, track, captureAttribution, getAttribution, sendCuratedEvent } from "@/lib/tracking";
import { toast } from "sonner";
import {
  Calendar, Users, Moon, Download, Loader2, MapPin, Clock, CheckCircle2, ChevronDown,
  FileText, Mail, Phone, X, Plus, Minus, ArrowRight, ShieldCheck, AlertCircle, BedDouble, Share2,
} from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Currency-aware formatter — /api/trip/{token} now echoes the itinerary's
// charge currency (USD default, EUR when the trip toggle is EUR, since
// 2026-08-17). Displayed as whole units so the presentation stays clean
// and matches what PayPal will actually charge.
const fmtUSD = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD",
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(n || 0);
const fmtEUR = (n) =>
  new Intl.NumberFormat("es-ES", {
    style: "currency", currency: "EUR",
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(n || 0);
const fmtAmount = (n, currency) =>
  (currency || "USD").toUpperCase() === "EUR" ? fmtEUR(n) : fmtUSD(n);

// "Mon" — three-letter month label used in the sticky day tabs, à la Fora.
const shortDay = (iso) => {
  if (!iso) return "";
  try {
    const d = parseDate(iso);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch { return iso; }
};

const longDayName = (iso) => {
  if (!iso) return "";
  try {
    return parseDate(iso).toLocaleDateString("en-US", { weekday: "long" });
  } catch { return ""; }
};

const longMonthDay = (iso) => {
  if (!iso) return "";
  try {
    return parseDate(iso).toLocaleDateString("en-US", { month: "long", day: "numeric" });
  } catch { return iso; }
};

const rangeShort = (a, b) => {
  if (!a || !b) return "";
  try {
    const s = parseDate(a).toLocaleDateString("en-US", { month: "short", day: "numeric" });
    const e = parseDate(b).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    return `${s} — ${e}`;
  } catch { return `${a} — ${b}`; }
};

const nightsBetween = (a, b) => {
  if (!a || !b) return null;
  try {
    const ms = parseDate(b) - parseDate(a);
    return Math.max(0, Math.round(ms / 86400000));
  } catch { return null; }
};

// Public curated pages never reveal the exact seat count. Only a scarcity
// nudge once the departure is down to its last seats (agency decision
// 2026-09-30: "no quiero que se vean las plazas disponibles, solo cuando
// quedan 6 'últimas plazas'").
const LAST_SEATS_THRESHOLD = 6;
const LAST_SEATS_LABEL = "Last seats available";
const isLastSeats = (n) => n > 0 && n <= LAST_SEATS_THRESHOLD;

const TYPE_KICKER = {
  transfer:   "TRANSFER",
  actividad:  "EXPERIENCE",
  activity:   "EXPERIENCE",
  vuelo:      "FLIGHT",
  flight:     "FLIGHT",
  alojamiento:"HOTEL",
  restaurante:"DINNER",
  restaurant: "DINNER",
};

// Coerce an API error `detail` field into a display string. FastAPI 422
// validation errors ship as `detail: [{loc, msg, type, ...}, ...]`; the
// modal renders `error` as a React child, so an unnormalised array
// crashes the whole tree ("Objects are not valid as a React child").
// Handles: string, array of pydantic-error objects, generic object, null.
function _apiErrorText(detail, fallback = "Something went wrong. Please try again.") {
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => {
      const loc = (d?.loc || []).slice(1).join(".") || "field";
      return `${loc}: ${d?.msg || "invalid"}`;
    }).join(" · ") || fallback;
  }
  if (typeof detail === "object") {
    return detail.msg || detail.detail || fallback;
  }
  return fallback;
}

export default function TripView({ curatedMode = false }) {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeDayId, setActiveDayId] = useState(null);
  const [reserveOpen, setReserveOpen] = useState(false);

  const dayRefs = useRef({});
  const heroRef = useRef(null);

  // Endpoint switch: /trip/{token} for regular itineraries vs
  // /curated/{token} for curated trips. The response shape is identical
  // (plus `is_curated`, `capacity`, `seats_available`, `price_single`,
  // `price_double` on curated) so the rest of the component is agnostic.
  const endpoint = curatedMode ? `curated/${token}` : `trip/${token}`;

  const load = async ({ silent = false } = {}) => {
    try {
      const { data: d } = await axios.get(`${API_BASE}/${endpoint}`);
      setData(d);
      if (!silent && d.days?.length) setActiveDayId(d.days[0].day_id);
      if (!silent && curatedMode) {
        sendCuratedEvent(token, "view");
        track("view_content", {
          content_id: d.public_token || token, content_name: d.trip_name,
          value: d.total_eur, currency: d.currency,
        });
      }
    } catch (e) {
      if (!silent) setError(_apiErrorText(e?.response?.data?.detail, "This trip link is not available."));
    } finally {
      if (!silent) setLoading(false);
    }
  };
  useEffect(() => { load(); }, [token]);

  // Marketing pixels + campaign attribution — curated (ad-driven) pages only.
  useEffect(() => {
    if (!curatedMode) return;
    captureAttribution();
    loadTracking();
  }, [curatedMode]);

  // "Share Trip" (curated): copy the clean public link — no UTM/campaign
  // params, so companions aren't counted as ad traffic.
  const shareTrip = async () => {
    const url = `${window.location.origin}/curated/${token}`;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement("textarea");
        ta.value = url; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select(); document.execCommand("copy"); ta.remove();
      }
      toast.success("Link copied! You can now share the trip link with your travel companions.", {
        position: "top-center", duration: 5000,
      });
      track("share_trip", { token });
    } catch {
      toast.error(`Couldn't copy automatically — here's the link: ${url}`, { position: "top-center", duration: 8000 });
    }
  };

  // Curated Trips hub chat widget ("Chat with us") — curated pages ONLY.
  // The script reads the token from /curated/<token>; we pass it explicitly
  // too. It self-disables inside the hub's iframe, so no extra guard here.
  useEffect(() => {
    if (!curatedMode || !token) return;
    const s = document.createElement("script");
    s.src = "https://curatedtrips.espiritutravel.com/widget.js";
    s.defer = true;
    s.dataset.trip = token;
    s.dataset.testid = "curated-chat-widget-script";
    document.body.appendChild(s);
    return () => { s.remove(); };
  }, [curatedMode, token]);
  useEffect(() => {
    if (curatedMode && reserveOpen && data) {
      sendCuratedEvent(token, "reserve_open");
      track("begin_checkout", {
        content_id: data.public_token || token, content_name: data.trip_name,
        value: data.total_eur, currency: data.currency,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reserveOpen]);

  // Auto-refresh the trip presentation when the client returns to the tab
  // (visibilitychange) or navigates back via browser cache (pageshow). If
  // the agent tweaked the itinerary while the client had this tab open, the
  // client will now see the latest days/photos/prices without pressing F5.
  useEffect(() => {
    const refresh = () => { if (!document.hidden) load({ silent: true }); };
    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("pageshow", refresh);
    return () => {
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener("pageshow", refresh);
    };
  }, [token]);

  // Track scroll — compute which day is currently in view so the nav can
  // highlight its tab.
  useEffect(() => {
    const onScroll = () => {
      // Find the day whose section top is closest to the top of the
      // viewport (but not yet scrolled past).
      const dayEntries = Object.entries(dayRefs.current);
      let bestId = null;
      let bestOffset = -Infinity;
      for (const [id, el] of dayEntries) {
        if (!el) continue;
        const top = el.getBoundingClientRect().top - 120;
        if (top <= 0 && top > bestOffset) { bestOffset = top; bestId = id; }
      }
      if (bestId) setActiveDayId(bestId);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [data]);

  const scrollToDay = (id) => {
    const el = dayRefs.current[id];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const cityLine = useMemo(
    () => (data?.cities || []).slice(0, 3).join(" & "),
    [data]
  );

  // Currency-aware formatter — every amount comes from /api/trip/ in the
  // trip's charge currency (`data.currency`), so we pick the right symbol
  // once and pass it wherever amounts are rendered.
  const currency = (data?.currency || "USD").toUpperCase();
  const fmt = (n) => fmtAmount(n, currency);

  // Refs for the two extra sticky-nav sections (Stay + Info) that the
  // client can jump to from the top navbar.
  const stayRef = useRef(null);
  const infoRef = useRef(null);
  const scrollToRef = (ref) => {
    if (ref?.current) ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center text-ink/70 font-sans">
        <Loader2 size={16} className="animate-spin mr-2"/> Loading your trip…
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <h1 className="font-serif text-3xl text-ink">Trip link unavailable</h1>
          <p className="mt-4 font-sans text-ink/70">{error}</p>
        </div>
      </div>
    );
  }

  const totalNights = nightsBetween(data.start_date, data.end_date);

  return (
    <div className="bg-cream text-ink min-h-screen font-sans">
      <ForaStyles />

      {/* --- Sticky top nav --------------------------------------------- */}
      {/* Always opaque. It used to be transparent while over the hero, but
          the dark ink text became unreadable on top of a full-bleed photo
          (reported 2026-09-16) — a plain bar is both legible and calmer. */}
      <nav
        className="fixed top-0 inset-x-0 z-40 bg-cream/95 backdrop-blur-md border-b border-ink/10"
        data-testid="trip-nav"
      >
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center gap-6">
          <img
            src="/espiritu/logo-horizontal.png"
            alt="Espíritu Travel"
            className="h-7 sm:h-8 w-auto"
          />
          {/* Day tabs + Stay + Info */}
          <div className="flex-1 hidden md:flex items-center justify-center gap-5 overflow-x-auto">
            {(data.days || []).map((d, di) => {
              const isActive = d.day_id === activeDayId;
              return (
                <button
                  key={d.day_id}
                  onClick={() => scrollToDay(d.day_id)}
                  data-testid={`day-tab-${d.day_id}`}
                  className={`whitespace-nowrap text-[13px] font-sans transition ${
                    isActive ? "text-ink font-semibold" : "text-ink/40 hover:text-ink/70"
                  }`}
                >
                  {shortDay(d.date) || `Day ${di + 1}`}
                </button>
              );
            })}
            {(data.accommodations || []).length > 0 && (
              <button
                onClick={() => scrollToRef(stayRef)}
                data-testid="nav-stay"
                className="whitespace-nowrap text-[13px] font-sans text-ink/40 hover:text-ink/70 transition"
              >
                Stay
              </button>
            )}
            <button
              onClick={() => scrollToRef(infoRef)}
              data-testid="nav-info"
              className="whitespace-nowrap text-[13px] font-sans text-ink/40 hover:text-ink/70 transition"
            >
              Info
            </button>
          </div>
          {/* CTA cluster */}
          <div className="flex items-center gap-2 shrink-0">
            {curatedMode ? (
              <button
                onClick={() => setReserveOpen(true)}
                data-testid="reserve-btn"
                disabled={data.seats_available <= 0}
                className="inline-flex items-center gap-2 bg-ink hover:bg-black disabled:bg-ink/30 disabled:cursor-not-allowed text-white px-4 py-2.5 rounded-full text-sm font-sans font-medium transition-colors shrink-0"
              >
                <CheckCircle2 size={14}/>
                {data.seats_available <= 0
                  ? "Sold out"
                  : (
                    <>
                      <span className="hidden sm:inline">
                        Reserve · from {fmt(Math.min(data.price_single || Infinity, data.price_double || Infinity))}
                      </span>
                      <span className="sm:hidden">Reserve</span>
                    </>
                  )}
              </button>
            ) : (
              <Link
                to={`/pay/${token}`}
                data-testid="reserve-btn"
                className="inline-flex items-center gap-2 bg-ink hover:bg-black text-white px-4 py-2.5 rounded-full text-sm font-sans font-medium transition-colors"
              >
                <CheckCircle2 size={14}/> Reserve{data.total_eur > 0 ? ` · ${fmt(data.total_eur)}` : ""}
              </Link>
            )}
            {curatedMode ? (
              <button
                onClick={shareTrip}
                data-testid="share-trip-btn"
                className="inline-flex items-center gap-2 text-ink/70 hover:text-ink px-3 py-2.5 text-sm font-sans"
              >
                <Share2 size={14}/> <span className="hidden sm:inline">Share Trip</span><span className="sm:hidden">Share</span>
              </button>
            ) : (
              <button
                onClick={() => window.print()}
                data-testid="download-btn"
                className="hidden sm:inline-flex items-center gap-2 text-ink/70 hover:text-ink px-3 py-2.5 text-sm font-sans"
              >
                <Download size={14}/> Download PDF
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* --- Hero -------------------------------------------------------- */}
      <header
        ref={heroRef}
        className="relative w-full min-h-[86vh] flex items-end overflow-hidden mt-[65px] sm:mt-[69px]"
        data-testid="hero"
      >
        {/* Hero photo when the trip has one of its own. Otherwise a solid
            ink field with a gold rule — never a generic stock photo that
            has nothing to do with the trip (agency feedback 2026-09-16). */}
        {data.hero_image ? (
          <>
            {/* <img> + srcset rather than a CSS background so phones don't
                download the 1600px variant (the hero is the heaviest asset
                on the page and these links are opened from email). */}
            <img
              src={data.hero_image}
              srcSet={data.hero_image_mobile && data.hero_image_mobile !== data.hero_image
                ? `${data.hero_image_mobile} 800w, ${data.hero_image} 1600w`
                : undefined}
              sizes="100vw"
              alt={data.trip_name || "Trip"}
              className="absolute inset-0 w-full h-full object-cover"
            />
            {/* Gradient overlay so text is readable */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/10 to-black/60"/>
          </>
        ) : (
          <div className="absolute inset-0" style={{ backgroundColor: "#12100e" }} data-testid="hero-editorial"/>
        )}

        {/* Agent pill top-right */}
        {data.agent?.name && (
          <div className="absolute top-8 right-6 sm:right-10 hidden sm:flex items-center gap-3 bg-white/12 backdrop-blur-md border border-white/20 rounded-full pl-1 pr-5 py-1">
            <div className="w-10 h-10 rounded-full bg-cream overflow-hidden flex items-center justify-center text-ink font-serif text-lg shrink-0">
              {data.agent.avatar_url ? (
                <img src={data.agent.avatar_url} alt={data.agent.name} className="w-full h-full object-cover"/>
              ) : (
                data.agent.name?.[0]?.toUpperCase() || "E"
              )}
            </div>
            <div className="text-white">
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/70">Your travel designer</div>
              <div className="font-sans text-sm">{data.agent.name}</div>
            </div>
          </div>
        )}

        <div className="relative w-full max-w-[1400px] mx-auto px-6 pb-16 pt-32 text-white">
          {!data.hero_image && (
            <div className="w-20 mb-8">
              <span className="hairline-gold" style={{ backgroundColor: "#B08749" }}/>
            </div>
          )}
          {curatedMode && (
            <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full bg-white/15 backdrop-blur-md border border-white/20" data-testid="curated-badge">
              <span className="w-1.5 h-1.5 rounded-full" style={{background: "#B08749"}}/>
              <span className="uppercase tracking-[0.22em] text-[10px] text-white/90 font-sans">
                Curated · Small group of {data.capacity}
              </span>
            </div>
          )}
          {cityLine && (
            <div className="inline-flex items-center gap-2 text-white/85 font-sans text-sm mb-6">
              <MapPin size={14}/> {cityLine}
            </div>
          )}
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] max-w-4xl" data-testid="trip-title">
            {data.trip_name}
          </h1>
          {curatedMode && data.tagline && (
            <div className="mt-4 font-serif text-2xl text-white/90 max-w-3xl italic" data-testid="hero-tagline">
              {data.tagline}
            </div>
          )}
          {data.main_traveler && !curatedMode && (
            <div className="mt-6 font-sans text-white/85 text-sm" data-testid="hero-prepared-for">
              <span className="uppercase tracking-[0.2em] text-[11px] text-white/60 mr-2">Prepared for</span>
              <span className="text-white text-[15px]">{data.main_traveler}</span>
            </div>
          )}
          <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 font-sans">
            <span className="inline-flex items-center gap-2 text-white/90 text-[15px]">
              <Calendar size={16}/> {rangeShort(data.start_date, data.end_date)}
            </span>
            {totalNights != null && (
              <span className="inline-flex items-center gap-2 text-white/90 text-[15px]">
                <Moon size={16}/> {totalNights} nights
              </span>
            )}
            {curatedMode ? (
              <>
                {data.seats_available <= 0 ? (
                  <span className="inline-flex items-center gap-2 text-white/90 text-[15px]" data-testid="hero-seats">
                    <Users size={16}/> Sold out
                  </span>
                ) : isLastSeats(data.seats_available) && (
                  <span className="inline-flex items-center gap-2 text-[15px] font-medium" style={{ color: "#E6C27A" }} data-testid="hero-seats">
                    <Users size={16}/> {LAST_SEATS_LABEL}
                  </span>
                )}
                {(data.price_single > 0 || data.price_double > 0) && (
                  <span className="inline-flex items-baseline gap-1.5 text-white text-[15px]" data-testid="hero-total">
                    <span className="text-white/60 text-[11px] uppercase tracking-[0.2em] mr-0.5">from</span>
                    <span className="font-medium">
                      {fmt(Math.min(data.price_single || Infinity, data.price_double || Infinity))}
                    </span>
                    <span className="text-white/60 text-[13px]">per person</span>
                  </span>
                )}
              </>
            ) : (
              <>
                {data.num_travelers && (
                  <span className="inline-flex items-center gap-2 text-white/90 text-[15px]">
                    <Users size={16}/> {data.num_travelers} travellers
                  </span>
                )}
                {data.total_eur > 0 && (
                  <span className="inline-flex items-baseline gap-1.5 text-white text-[15px]" data-testid="hero-total">
                    <span className="text-white/60 text-[11px] uppercase tracking-[0.2em] mr-0.5">from</span>
                    <span className="font-medium">{fmt(data.total_eur)}</span>
                    <span className="text-white/60 text-[13px]">total</span>
                  </span>
                )}
              </>
            )}
          </div>

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/60 text-[10px] tracking-[0.3em] uppercase inline-flex flex-col items-center gap-2">
            Scroll to view
            <ChevronDown size={16} className="animate-bounce"/>
          </div>
        </div>
      </header>

      {/* --- Journey intro paragraph ------------------------------------ */}
      {(data.summary || true) && (
        <section className="max-w-3xl mx-auto px-6 py-24 sm:py-32 text-center">
          <div className="kicker mb-8">Your journey ahead</div>
          <p className="font-serif text-2xl sm:text-3xl leading-[1.35] text-ink/85">
            {data.summary ||
              `A hand-crafted journey through ${cityLine || "your chosen destinations"}. Slow mornings, private access, and dinners chosen for their soul rather than their address.`}
          </p>
        </section>
      )}

      {/* --- Trip at a glance (compact multi-day summary) --------------- */}
      {(data.days || []).length > 0 && (
        <section className="border-t border-ink/10">
          <div className="max-w-[1400px] mx-auto px-6 py-16 sm:py-20">
            <div className="max-w-5xl mx-auto">
              <div className="kicker mb-3 text-center">Trip at a glance</div>
              <h2 className="font-serif text-3xl sm:text-4xl text-ink text-center mb-14">
                Your journey day by day
              </h2>
              <ul className="divide-y divide-ink/10" data-testid="trip-glance">
                {(data.days || []).map((d, idx) => (
                  <li key={d.day_id} className="py-6 grid grid-cols-1 sm:grid-cols-[110px_160px_1fr] gap-x-6 gap-y-2 sm:items-start">
                    <div className="flex sm:block items-baseline gap-3">
                      <div className="kicker">Day {idx + 1}</div>
                      <div className="font-sans text-ink/60 text-[13px] sm:mt-1 tabular">
                        {shortDay(d.date)}
                      </div>
                    </div>
                    <div className="font-serif text-xl text-ink">
                      {d.city || "—"}
                    </div>
                    <ul className="font-sans text-[14px] text-ink/80 space-y-1.5 min-w-0">
                      {(d.services || []).length === 0 ? (
                        <li className="text-ink/40 italic">Free day</li>
                      ) : (
                        (d.services || []).map((s, i) => (
                          <li key={s.id || i} className="flex items-start gap-2 min-w-0">
                            <span className="mt-1.5 shrink-0 w-1 h-1 rounded-full" style={{background: "#B08749"}}/>
                            <span className="break-words min-w-0">{s.name}</span>
                          </li>
                        ))
                      )}
                    </ul>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* --- Days -------------------------------------------------------- */}
      <div className="border-t border-ink/10 max-w-[1400px] mx-auto">
        {(data.days || []).map((d, di) => (
          <DaySection
            key={d.day_id}
            day={d}
            index={di}
            innerRef={(el) => { dayRefs.current[d.day_id] = el; }}
            accommodations={data.accommodations}
          />
        ))}
      </div>

      {/* --- Where you'll stay (all accommodations grouped) ------------- */}
      {(data.accommodations || []).length > 0 && (
        <section ref={stayRef} className="border-t border-ink/10 bg-white/40">
          <div className="max-w-[1400px] mx-auto px-6 py-20 sm:py-24">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-14">
                <div className="kicker mb-3">Where you&apos;ll stay</div>
                <h2 className="font-serif text-3xl sm:text-4xl text-ink">Your accommodations</h2>
              </div>
              <div className="space-y-14">
                {(data.accommodations || []).map((a) => (
                  <StayCard key={a.acc_id} accommodation={a} perks={data.hotel_perks}/>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* --- Information & Documents ----------------------------------- */}
      <section ref={infoRef} className="border-t border-ink/10">
        <div className="max-w-[1400px] mx-auto px-6 py-20 sm:py-24">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-14">
              <div className="kicker mb-3">Good to know</div>
              <h2 className="font-serif text-3xl sm:text-4xl text-ink">Information &amp; Documents</h2>
            </div>
            <InfoDocuments overrides={data.info_documents} depositPct={data.deposit_pct || 30}/>
          </div>
        </div>
      </section>

      {/* --- Designed for you by --------------------------------------- */}
      {data.agent?.name && (
        <section className="border-t border-ink/10 bg-white/40">
          <div className="max-w-[1400px] mx-auto px-6 py-20 sm:py-24">
            <div className="max-w-3xl mx-auto text-center">
              <div className="kicker mb-6">Designed for you by</div>
              {data.agent.avatar_url && (
                <div className="w-28 h-28 mx-auto mb-6 rounded-full overflow-hidden border border-ink/10 bg-white" data-testid="agent-avatar">
                  <img src={data.agent.avatar_url} alt={data.agent.name} className="w-full h-full object-cover"/>
                </div>
              )}
              <h3 className="font-serif text-4xl text-ink" data-testid="agent-name">{data.agent.name}</h3>
              <div className="mt-2 font-sans text-ink/60 text-sm tracking-wider uppercase">Travel Specialist</div>
              {data.agent.email && (
                <a
                  href={`mailto:${data.agent.email}`}
                  data-testid="agent-email"
                  className="mt-5 inline-flex items-center gap-2 font-sans text-ink underline decoration-ink/30 hover:decoration-ink transition text-[15px]"
                >
                  <Mail size={14}/> {data.agent.email}
                </a>
              )}
              <div className="mt-8">
                <a
                  href={data.agent.calendar_url || `mailto:${data.agent.email}?subject=${encodeURIComponent(`Book a call about ${data.trip_name || "my trip"}`)}`}
                  data-testid="book-a-call-btn"
                  className="inline-flex items-center gap-2 border border-ink hover:bg-ink hover:text-white text-ink px-6 py-3 rounded-full text-sm font-sans font-medium transition-colors"
                >
                  <Phone size={13}/> Book a call
                </a>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* --- Footer CTA -------------------------------------------------- */}
      <footer className="border-t border-ink/10 mt-24">
        <div className="max-w-3xl mx-auto px-6 py-20 text-center">
          <div className="kicker mb-4">Ready to make it real?</div>
          <h2 className="font-serif text-4xl sm:text-5xl text-ink">
            {curatedMode ? "Reserve your spot" : "Confirm your trip"}
          </h2>
          <p className="font-sans mt-6 text-ink/70 max-w-lg mx-auto">
            {curatedMode ? (
              data.seats_available > 0
                ? `${isLastSeats(data.seats_available) ? `${LAST_SEATS_LABEL} — don't miss out. ` : ""}Your seats are held for ${data.hold_minutes || 10} minutes once you start checkout — reservation is confirmed as soon as payment is captured.`
                : "This departure is fully booked. Reach out to your travel designer to be added to the waitlist."
            ) : (
              "Secure your dates today. You can pay a 30% deposit or the full amount — either way, we start booking your services as soon as it's confirmed."
            )}
          </p>
          {curatedMode ? (
            <button
              onClick={() => setReserveOpen(true)}
              disabled={data.seats_available <= 0}
              data-testid="footer-reserve-btn"
              className="inline-flex items-center gap-2 bg-ink hover:bg-black disabled:bg-ink/30 disabled:cursor-not-allowed text-white px-6 py-3.5 rounded-full text-sm font-medium mt-10"
            >
              <CheckCircle2 size={14}/>
              {data.seats_available > 0 ? "Reserve your spot" : "Sold out"}
            </button>
          ) : (
            <Link
              to={`/pay/${token}`}
              data-testid="footer-reserve-btn"
              className="inline-flex items-center gap-2 bg-ink hover:bg-black text-white px-6 py-3.5 rounded-full text-sm font-medium mt-10"
            >
              <CheckCircle2 size={14}/> Reserve{data.total_eur > 0 ? ` · ${fmt(data.total_eur)}` : ""}
            </Link>
          )}
        </div>
        <div className="border-t border-ink/10">
          <div className="max-w-[1400px] mx-auto px-6 py-5 flex items-center justify-between font-sans text-[11px] text-ink/50">
            <div>© Espíritu Travel · All rights reserved</div>
            <div>Need help? Reach out to {data.agent?.name || "your travel designer"}.</div>
          </div>
        </div>
      </footer>

      {/* Curated reservation modal — mounted at the root so it can portal
          above the sticky nav. Only rendered when the trip is a curated
          product (see curatedMode prop). */}
      {curatedMode && (
        <CuratedReserveModal
          open={reserveOpen}
          onClose={() => setReserveOpen(false)}
          trip={data}
          publicToken={token}
          fmt={fmt}
        />
      )}
    </div>
  );
}

/* --- Sub-components ------------------------------------------------- */

function DaySection({ day, index = 0, innerRef }) {
  // When NOTHING in the day carries a photo we switch to the editorial
  // treatment: one wide destination band as the anchor + text blocks with
  // gold rules. No fake stock photos inside the activities themselves
  // (decided 2026-09-16 with the client: "si no hay fotos, adapta el
  // diseño para que quede bien").
  const dayHasPhotos = (day.services || []).some(
    (s) => (s.image_urls || []).filter(Boolean).length > 0,
  );
  const band = dayHasPhotos ? null : day.image_url;
  // Travefy itineraries are often published without calendar dates. The
  // header then falls back to "Day N" + the Travefy day title instead of
  // leaving a blank slot where the big serif date should be.
  const dayNo = `Day ${index + 1}`;
  const hasDate = Boolean(day.date);
  // "Day 4" / "Día 4" as a title carries no information (it's the
  // internal label of AI-built itineraries) — treat it as empty so it
  // never reaches the client-facing page.
  const titleIsUseful = Boolean(
    day.label && day.label !== day.city
    && !/^(day|d[ií]a|jour|giorno|tag)\s*\d+$/i.test(day.label.trim()),
  );
  const heading = hasDate ? longMonthDay(day.date) : (titleIsUseful ? day.label : dayNo);
  const kickerText = hasDate ? longDayName(day.date) : (titleIsUseful ? dayNo : "");
  return (
    <section
      ref={innerRef}
      data-testid={`day-section-${day.day_id}`}
      className="py-16 sm:py-24 border-b border-ink/10 last:border-b-0"
    >
      {/* Day header — narrow left column with day-of-week + big serif date */}
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="max-w-3xl mx-auto md:mx-0 md:ml-[calc(20%_+_1rem)]" data-testid="day-section-header">
          {kickerText && <div className="kicker mb-2">{kickerText}</div>}
          <h2 className="font-serif text-5xl sm:text-6xl text-ink leading-[1.05]">
            {heading}
          </h2>
          <div className="mt-4 flex items-center gap-4">
            {day.city && (
              <span className="font-sans text-ink/60 inline-flex items-center gap-2 text-sm shrink-0">
                <MapPin size={14}/> {day.city}
              </span>
            )}
            <span className="hairline-gold flex-1"/>
          </div>
          {titleIsUseful && heading !== day.label && (
            <div className="mt-4 font-serif text-2xl sm:text-3xl text-ink/75 italic leading-snug" data-testid="day-title">
              {day.label}
            </div>
          )}
        </div>
      </div>

      {band && (
        <motion.div
          className="max-w-[1200px] mx-auto px-6 mt-10"
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          <div className="aspect-[21/9] overflow-hidden bg-ink/5" data-testid="day-band">
            <img
              src={band}
              alt={day.city || "Destination"}
              loading="lazy"
              className="w-full h-full object-cover"
            />
          </div>
        </motion.div>
      )}

      {/* Services */}
      <div className="max-w-[1200px] mx-auto px-6 mt-12 space-y-14">
        {(day.services || []).map((s, i) => (
          <ServiceRow key={s.id || i} service={s} index={i}/>
        ))}
      </div>
    </section>
  );
}

/** Travefy descriptions arrive as plain text where list items were
 *  flattened into "• item" lines. Split them so the presentation can
 *  render real paragraphs and a styled gold-dot list. */
function splitDescription(raw) {
  const lines = String(raw || "").split("\n").map((l) => l.trim()).filter(Boolean);
  const paragraphs = [];
  const bullets = [];
  for (const line of lines) {
    if (/^[•·*]\s+/.test(line) || /^[-–]\s+\S/.test(line)) {
      bullets.push(line.replace(/^[•·*\-–]\s+/, ""));
    } else {
      paragraphs.push(line);
    }
  }
  return { paragraphs, bullets };
}

function GoldBullets({ items, className = "" }) {
  if (!items?.length) return null;
  return (
    <ul className={`font-sans text-[15px] text-ink/80 space-y-2 leading-relaxed ${className}`} data-testid="gold-bullets">
      {items.map((b, i) => (
        <li key={i} className="flex items-start gap-3">
          <span className="mt-[9px] shrink-0 w-1 h-1 rounded-full" style={{ background: "#B08749" }}/>
          <span>{b}</span>
        </li>
      ))}
    </ul>
  );
}

function ServiceRow({ service, index = 0 }) {
  const kicker = TYPE_KICKER[service.type] || service.type?.toUpperCase() || "EXPERIENCE";
  const gallery = (service.image_urls || []).filter(Boolean);
  if (!gallery.length && service.image_url) gallery.push(service.image_url);
  const hasPhotos = gallery.length > 0;
  const { paragraphs, bullets } = splitDescription(service.description);
  // A row like "Meals Included · Dinner" is a short informative note, not
  // an experience: render it as a compact callout instead of a full block.
  const isShortInfo = !hasPhotos && paragraphs.length === 0 && bullets.length > 0 && bullets.length <= 4;

  const reveal = {
    initial: { opacity: 0, y: 16 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.2 },
    transition: { duration: 0.45, ease: "easeOut", delay: Math.min(index, 3) * 0.05 },
  };

  if (isShortInfo) {
    return (
      <motion.div className="grid md:grid-cols-[20%_1fr] gap-6 md:gap-10 items-start" data-testid="short-info-callout" {...reveal}>
        <div className="kicker pt-1">{kicker}</div>
        <div className="paper-callout max-w-2xl px-6 py-5">
          <div className="font-serif text-xl text-ink">{service.name}</div>
          <ul className="mt-3 flex flex-wrap gap-x-7 gap-y-2">
            {bullets.map((b, i) => (
              <li key={i} className="inline-flex items-center gap-2 font-sans text-[14px] text-ink/75">
                <span className="w-1 h-1 rounded-full" style={{ background: "#B08749" }}/> {b}
              </li>
            ))}
          </ul>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div className="grid md:grid-cols-[20%_1fr] gap-6 md:gap-10 items-start" data-testid="service-row" {...reveal}>
      <div>
        <div className="kicker">{kicker}</div>
        {service.time && (
          <div className="font-serif text-2xl text-ink mt-2">{service.time}</div>
        )}
        {service.duration && (
          <div className="font-sans text-ink/60 text-sm mt-1 inline-flex items-center gap-1.5">
            <Clock size={12}/> {service.duration}
          </div>
        )}
      </div>
      {/* Without photos the block leans on typography: a gold rule anchors
          the column, the measure narrows and the text breathes. */}
      <div
        className={hasPhotos ? "" : "border-l pl-6 sm:pl-8"}
        style={hasPhotos ? undefined : { borderColor: "rgba(176,135,73,0.35)" }}
        data-testid={hasPhotos ? "activity-with-photos" : "editorial-no-photo-block"}
      >
        <h3 className="font-serif text-2xl sm:text-3xl text-ink leading-tight" data-testid="activity-title">
          {service.name}
        </h3>
        {(service.meeting_point || service.time) && (
          <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 font-sans text-[13px] text-ink/70 max-w-lg">
            {service.meeting_point && (
              <div><span className="text-ink/50">Meeting Point:</span> <span className="text-ink">{service.meeting_point}</span></div>
            )}
            {service.time && (
              <div><span className="text-ink/50">Meeting Time:</span> <span className="text-ink">{service.time}</span></div>
            )}
            {service.duration && (
              <div><span className="text-ink/50">Duration:</span> <span className="text-ink">{service.duration}</span></div>
            )}
          </div>
        )}
        {paragraphs.length > 0 && (
          <div className={`mt-5 space-y-4 max-w-2xl font-sans text-ink/80 ${hasPhotos ? "text-[15px] leading-relaxed" : "text-[16px] leading-[1.8]"}`}>
            {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
          </div>
        )}
        <GoldBullets items={bullets} className="mt-5 max-w-2xl"/>
        <PhotoGallery photos={gallery} alt={service.name} className="mt-7 max-w-2xl" testid="service-gallery"/>
      </div>
    </motion.div>
  );
}

/** Photo mosaic that adapts to however many photos the trip actually has.
 *  Travefy itineraries carry anywhere from 0 to 6+ photos per activity, so
 *  a fixed "hero + 2 thumbs" layout either wasted them or left holes
 *  (reported 2026-09-16).
 *    1 → single wide       2 → side by side       3 → hero + 2
 *    4 → 2×2               5+ → hero + 3-up rows
 */
function PhotoGallery({ photos = [], alt = "", className = "", testid }) {
  const list = (photos || []).filter(Boolean).slice(0, 7);
  const n = list.length;
  if (!n) return null;
  const Img = ({ src, ratio, i }) => (
    <div className={`${ratio} overflow-hidden bg-ink/5`}>
      <img
        src={src}
        alt={i ? `${alt} ${i + 1}` : alt}
        loading="lazy"
        className="w-full h-full object-cover transition-transform duration-700 hover:scale-[1.03]"
      />
    </div>
  );
  return (
    <div className={className} data-testid={testid} data-photos={n}>
      {n === 1 && <Img src={list[0]} ratio="aspect-[16/9]" i={0}/>}
      {(n === 2 || n === 4) && (
        <div className="grid grid-cols-2 gap-3">
          {list.map((u, i) => <Img key={i} src={u} ratio="aspect-[4/3]" i={i}/>)}
        </div>
      )}
      {n === 6 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {list.map((u, i) => <Img key={i} src={u} ratio="aspect-[4/3]" i={i}/>)}
        </div>
      )}
      {(n === 3 || n === 5 || n === 7) && (
        <>
          <Img src={list[0]} ratio="aspect-[16/9]" i={0}/>
          <div className={`mt-3 grid gap-3 ${n === 7 ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-2"}`}>
            {list.slice(1).map((u, i) => <Img key={i} src={u} ratio="aspect-[4/3]" i={i + 1}/>)}
          </div>
        </>
      )}
    </div>
  );
}

// `perks` = the client advantages list, resolved server-side (trip's own →
// agency template → code defaults). An empty list hides the column.
function StayCard({ accommodation: a, perks }) {
  const perkList = (Array.isArray(perks) ? perks : DEFAULT_HOTEL_PERKS).filter(Boolean);
  const nights = nightsBetween(a.date_from, a.date_to);
  const images = (a.image_urls || []).filter(Boolean);
  const hasPhotos = images.length > 0;
  // Travefy hotel descriptions arrive in `notes`; without photos they
  // become the centrepiece of the card, so they get real paragraphs and
  // gold-dot lists instead of being dropped (2026-09-16).
  const { paragraphs, bullets } = splitDescription(a.notes);
  const hasDetails = Boolean(a.address) || (a.rooms?.length > 0);
  const stayRange = () => {
    if (!a.date_from || !a.date_to) return "";
    try {
      const dt = (iso, opts) => parseDate(iso).toLocaleDateString("en-US", opts);
      const s = dt(a.date_from, { weekday: "short", month: "short", day: "numeric" });
      const e = dt(a.date_to, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
      return `${s} — ${e}`;
    } catch { return ""; }
  };
  return (
    <div
      className={`border ${hasPhotos ? "border-ink/10 bg-white" : ""}`}
      style={hasPhotos ? undefined : { borderColor: "rgba(176,135,73,0.32)", backgroundColor: "#fffdfa" }}
      data-testid="stay-card"
      data-photos={images.length}
    >
      <div className="px-6 sm:px-10 pt-8 pb-6 flex items-start justify-between gap-6 flex-wrap">
        <div className="flex items-start gap-5">
          {!hasPhotos && (
            <div
              className="hidden sm:flex w-14 h-14 rounded-full bg-gold-light items-center justify-center shrink-0 border"
              style={{ borderColor: "rgba(176,135,73,0.3)" }}
              data-testid="stay-no-photo-badge"
            >
              <BedDouble size={22} className="text-gold"/>
            </div>
          )}
          <div>
            <div className="kicker mb-2">Where you stay</div>
            <h3 className="font-serif text-4xl text-ink leading-tight">{a.name}</h3>
            {a.city && (
              <div className="mt-2 font-sans text-sm text-ink/60 inline-flex items-center gap-1.5">
                <MapPin size={13}/> {a.city}
              </div>
            )}
          </div>
        </div>
        <div className="text-right font-sans text-sm text-ink/70 pt-2 inline-flex items-center gap-2">
          <Moon size={13}/> {stayRange()}{nights ? ` · ${nights} nights` : ""}
        </div>
      </div>
      {hasPhotos ? (
        <div className="px-6 sm:px-10">
          <PhotoGallery photos={images} alt={a.name} testid="stay-gallery"/>
        </div>
      ) : (
        <div className="px-6 sm:px-10"><span className="hairline-gold"/></div>
      )}
      {(paragraphs.length > 0 || bullets.length > 0) && (
        <div className="px-6 sm:px-10 mt-7 max-w-3xl" data-testid="stay-description">
          <div className={`space-y-4 font-sans text-ink/80 ${hasPhotos ? "text-[15px] leading-relaxed" : "text-[16px] leading-[1.8]"}`}>
            {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
          </div>
          <GoldBullets items={bullets} className="mt-4"/>
        </div>
      )}
      {(hasDetails || perkList.length > 0) && (
      <div className={`px-6 sm:px-10 py-8 grid gap-8 border-t border-ink/10 mt-8 ${hasDetails && perkList.length > 0 ? "md:grid-cols-2" : ""}`}>
        <div className={hasDetails ? "" : "hidden"}>
          {a.address && (
            <div>
              <div className="kicker mb-1.5">Address</div>
              <div className="font-sans text-[14px] text-ink inline-flex items-start gap-1.5">
                <MapPin size={14} className="mt-0.5 shrink-0"/> {a.address}
              </div>
            </div>
          )}
          {a.rooms?.length > 0 && (
            <div className="mt-6">
              <div className="kicker mb-1.5">Your rooms</div>
              <ul className="font-sans text-[14px] text-ink space-y-0.5">
                {a.rooms.map((r, i) => (
                  <li key={i}>{`1 × ${r.type || "Room"}`}{r.size_sqm ? ` — ${r.size_sqm} m²` : ""}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
        {perkList.length > 0 && (
          <div className="max-w-xl" data-testid="stay-perks">
            <div className="kicker mb-1.5">{HOTEL_PERKS_TITLE}</div>
            <ul className="font-sans text-[14px] text-ink space-y-1.5">
              {perkList.map((line) => (
                <li key={line} className="inline-flex items-start gap-2">
                  <CheckCircle2 size={14} className="text-emerald-700 mt-0.5 shrink-0"/> {line}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      )}
    </div>
  );
}

/* --- Information & Documents ---------------------------------------- */

// The block definitions live in `lib/infoBlocks` so the curated builder
// edits exactly what the client sees. Resolution order: the trip's own
// edited blocks → the agency template (resolved server-side and merged
// into `info_documents` by the API) → the code defaults.
function InfoDocuments({ overrides = {}, depositPct = 30 }) {
  const blocks = resolveInfoBlocks(overrides, depositPct);
  return (
    <div className="grid gap-10 md:grid-cols-2" data-testid="info-documents">
      {blocks.map((b) => (
        <div key={b.key} data-testid={`info-block-${b.key}`}>
          <div className="inline-flex items-center gap-2 kicker mb-4">
            <FileText size={12}/> {b.title}
          </div>
          <ul className="font-sans text-[14.5px] text-ink/85 space-y-2 leading-relaxed">
            {(b.bullets || []).map((line, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span className="mt-2 shrink-0 w-1 h-1 rounded-full" style={{background: "#B08749"}}/>
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

/* --- Curated reservation modal --------------------------------------- */

// Three-step reservation flow for curated small-group trips:
//   1. Number of travellers (bounded by seats_available).
//   2. Per-seat room type — single (private) or double (shared). When the
//      buyer picks "double" and the seat can't be paired inside their own
//      party (lone traveller or odd remainder) we require explicit consent
//      to share with a stranger via a checkbox with the copy the client
//      requested verbatim: "I'm willing to share with a stranger if space
//      allows".
//   3. Buyer name + email + T&C acknowledgment. The 10-min soft-hold
//      warning is shown here so the buyer knows the clock starts once
//      they hit "Continue to payment".
// On submit → POST /api/curated/{public_token}/hold → navigate to
// /pay/curated/{payment_token} (PublicCuratedPayment).
function CuratedReserveModal({ open, onClose, trip, publicToken, fmt }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [seats, setSeats] = useState(1);
  const [rooms, setRooms] = useState([{ room_type: "double", willing_to_share_with_stranger: false }]);
  const [buyerName, setBuyerName] = useState("");
  const [buyerEmail, setBuyerEmail] = useState("");
  const [tosAccepted, setTosAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Reset state whenever the modal is (re)opened so a previous half-filled
  // attempt doesn't leak into the next buyer's flow.
  useEffect(() => {
    if (open) {
      setStep(1);
      setSeats(1);
      setRooms([{ room_type: "double", willing_to_share_with_stranger: false }]);
      setBuyerName("");
      setBuyerEmail("");
      setTosAccepted(false);
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  // Keep the `rooms` array length in sync with `seats`. When shrinking we
  // trim the tail; when growing we default new seats to "double" (the
  // cheaper option, which is also the most common in group tours).
  useEffect(() => {
    setRooms((prev) => {
      if (prev.length === seats) return prev;
      if (prev.length > seats) return prev.slice(0, seats);
      const filler = Array.from({ length: seats - prev.length }, () => ({
        room_type: "double", willing_to_share_with_stranger: false,
      }));
      return [...prev, ...filler];
    });
  }, [seats]);

  const seatsAvailable = trip?.seats_available || 0;
  const priceSingle = trip?.price_single || 0;
  const priceDouble = trip?.price_double || 0;
  const total = rooms.reduce(
    (sum, r) => sum + (r.room_type === "single" ? priceSingle : priceDouble),
    0,
  );

  // For each `double` seat, decide whether it must show the "willing to
  // share with a stranger" checkbox. Rule: if the count of double seats
  // in this booking is odd, the last one is unpaired and requires the
  // consent flag. Simpler UX: show the checkbox on every double seat and
  // let the buyer decide — they know their own party better than we do.
  const doubleCount = rooms.filter((r) => r.room_type === "double").length;
  const requiresStrangerConsent = doubleCount > 0 && doubleCount % 2 === 1;

  const updateRoom = (idx, patch) => {
    setRooms((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API_BASE}/curated/${publicToken}/hold`, {
        buyer_name: buyerName.trim(),
        buyer_email: buyerEmail.trim(),
        tos_accepted: tosAccepted,
        tos_version: "espiritutravel-tos-v1",
        origin: window.location.origin,
        attribution: getAttribution(),
        room_selections: rooms.map((r) => ({
          room_type: r.room_type,
          willing_to_share_with_stranger: !!r.willing_to_share_with_stranger,
        })),
      });
      track("add_payment_info", {
        content_id: publicToken, content_name: trip?.trip_name,
        value: total, currency: trip?.currency, quantity: seats,
      });
      // Success — jump to the buyer's own payment page with the fresh
      // payment_token. The page shows the 10-min countdown + PayPal CTA.
      navigate(`/pay/curated/${data.payment_token}`);
    } catch (e) {
      setError(_apiErrorText(e?.response?.data?.detail, "Something went wrong. Please try again."));
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const canNext1 = seats >= 1 && seats <= seatsAvailable;
  // Every unpaired double must have consent checked (or the seat must be
  // switched to single). Enforced also server-side (see /hold endpoint).
  const canNext2 = rooms.every((r) => {
    if (r.room_type !== "double") return true;
    // If the group has an odd number of doubles → last one shares w/ stranger
    return true;
  }) && (!requiresStrangerConsent || rooms.some((r) => r.room_type === "double" && r.willing_to_share_with_stranger));
  const canSubmit = buyerName.trim().length >= 2 &&
                    /\S+@\S+\.\S+/.test(buyerEmail) &&
                    tosAccepted &&
                    !submitting;

  return (
    <div
      className="fixed inset-0 z-50 bg-ink/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6 overflow-y-auto"
      onClick={onClose}
      data-testid="curated-reserve-modal"
    >
      <div
        className="bg-cream w-full sm:max-w-2xl sm:rounded-xl shadow-xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="modal-close"
          className="absolute top-4 right-4 text-ink/60 hover:text-ink z-10"
          aria-label="Close"
        >
          <X size={20}/>
        </button>

        {/* Header */}
        <div className="px-6 sm:px-10 pt-8 pb-6 border-b border-ink/10">
          <div className="kicker mb-2">Reserve your spot</div>
          <h2 className="font-serif text-2xl sm:text-3xl text-ink">
            {trip?.trip_name}
          </h2>
          <div className="mt-2 font-sans text-[13px] text-ink/60 tabular" data-testid="modal-dates">
            {rangeShort(trip?.start_date, trip?.end_date)}
            {isLastSeats(seatsAvailable) && (
              <span className="ml-2 font-medium" style={{ color: "#B08749" }} data-testid="modal-last-seats">· {LAST_SEATS_LABEL}</span>
            )}
          </div>
          {/* Step indicator */}
          <div className="mt-6 flex items-center gap-2 font-sans text-[11px] uppercase tracking-[0.18em]">
            {[1, 2, 3].map((s) => (
              <React.Fragment key={s}>
                <span
                  className={`inline-flex items-center justify-center w-6 h-6 rounded-full border ${
                    step >= s ? "bg-ink text-white border-ink" : "text-ink/40 border-ink/20"
                  }`}
                >
                  {s}
                </span>
                {s < 3 && <span className={`h-px w-6 ${step > s ? "bg-ink" : "bg-ink/15"}`}/>}
              </React.Fragment>
            ))}
            <span className="ml-3 text-ink/60">
              {step === 1 ? "How many spots" : step === 2 ? "Room type" : "Your details"}
            </span>
          </div>
        </div>

        {/* Step body */}
        <div className="px-6 sm:px-10 py-8 min-h-[240px]">
          {step === 1 && (
            <div data-testid="step-seats">
              <div className="kicker mb-4">Travellers</div>
              <p className="font-sans text-ink/70 text-[14.5px] mb-6 leading-relaxed">
                How many spots would you like to reserve? Each traveller picks their room type in the next step.
              </p>
              <div className="inline-flex items-center gap-4 border border-ink/15 rounded-full px-2 py-1.5 bg-white">
                <button
                  onClick={() => setSeats(Math.max(1, seats - 1))}
                  disabled={seats <= 1}
                  data-testid="seats-minus"
                  className="w-10 h-10 rounded-full border border-ink/10 hover:bg-ink/5 disabled:opacity-30 flex items-center justify-center text-ink"
                >
                  <Minus size={16}/>
                </button>
                <div className="min-w-[64px] text-center">
                  <div className="font-serif text-4xl text-ink leading-none tabular" data-testid="seats-value">{seats}</div>
                  <div className="font-sans text-[11px] text-ink/50 tracking-wider uppercase mt-1">
                    {seats === 1 ? "traveller" : "travellers"}
                  </div>
                </div>
                <button
                  onClick={() => setSeats(Math.min(seatsAvailable, seats + 1))}
                  disabled={seats >= seatsAvailable}
                  data-testid="seats-plus"
                  className="w-10 h-10 rounded-full border border-ink/10 hover:bg-ink/5 disabled:opacity-30 flex items-center justify-center text-ink"
                >
                  <Plus size={16}/>
                </button>
              </div>
              {seats >= seatsAvailable && (
                <div className="mt-4 font-sans text-[12px] text-ink/50" data-testid="seats-max-note">
                  That&apos;s the maximum we can reserve on this departure
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div data-testid="step-rooms">
              <div className="kicker mb-4">Room type per traveller</div>
              <p className="font-sans text-ink/70 text-[14.5px] mb-6 leading-relaxed">
                Choose a private single or a shared double for each traveller. Solo travellers picking &ldquo;double&rdquo; can opt in to share with another traveller in the group.
              </p>
              <div className="space-y-4">
                {rooms.map((r, idx) => (
                  <div key={idx} className="border border-ink/10 rounded-lg p-4 bg-white" data-testid={`seat-row-${idx}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="font-sans text-[12px] uppercase tracking-[0.18em] text-ink/60">
                        Traveller {idx + 1}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { key: "single", label: "Single room", detail: "Private", price: priceSingle },
                        { key: "double", label: "Double room", detail: "Shared", price: priceDouble },
                      ].map((opt) => {
                        const active = r.room_type === opt.key;
                        return (
                          <button
                            key={opt.key}
                            onClick={() => updateRoom(idx, { room_type: opt.key })}
                            data-testid={`room-${opt.key}-${idx}`}
                            className={`text-left rounded-lg border p-3 transition ${
                              active
                                ? "border-ink bg-ink/[0.03] ring-1 ring-ink"
                                : "border-ink/15 hover:border-ink/30"
                            }`}
                          >
                            <div className="font-serif text-lg text-ink">{opt.label}</div>
                            <div className="font-sans text-[12px] text-ink/50 mt-0.5">{opt.detail}</div>
                            <div className="font-sans text-[15px] text-ink mt-2 tabular">
                              {fmt(opt.price)} <span className="text-ink/50 text-[12px]">/ person</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    {r.room_type === "double" && (
                      <label className="mt-3 flex items-start gap-2.5 cursor-pointer" data-testid={`willing-share-${idx}`}>
                        <input
                          type="checkbox"
                          checked={!!r.willing_to_share_with_stranger}
                          onChange={(e) => updateRoom(idx, { willing_to_share_with_stranger: e.target.checked })}
                          className="mt-1 accent-ink"
                        />
                        <span className="font-sans text-[13px] text-ink/75 leading-relaxed">
                          I&apos;m willing to share with a stranger if space allows
                        </span>
                      </label>
                    )}
                  </div>
                ))}
              </div>
              {requiresStrangerConsent && !rooms.some((r) => r.room_type === "double" && r.willing_to_share_with_stranger) && (
                <div className="mt-4 flex items-start gap-2 text-[13px] text-espiritu-terra font-sans" style={{color: "#B08749"}} data-testid="stranger-consent-warning">
                  <AlertCircle size={14} className="mt-0.5 shrink-0"/>
                  <span>You have an unpaired double room. Check the &ldquo;willing to share&rdquo; box on one of them, or switch it to a single.</span>
                </div>
              )}
              <div className="mt-6 pt-4 border-t border-ink/10 flex items-baseline justify-between font-sans">
                <div className="text-ink/60 text-[12px] uppercase tracking-wider">Total</div>
                <div className="font-serif text-3xl text-ink tabular" data-testid="modal-total">{fmt(total)}</div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="step-buyer">
              <div className="kicker mb-4">Your details</div>
              <div className="grid gap-4">
                <label className="block">
                  <span className="font-sans text-[12px] uppercase tracking-[0.18em] text-ink/60">Full name</span>
                  <input
                    type="text"
                    value={buyerName}
                    onChange={(e) => setBuyerName(e.target.value)}
                    data-testid="buyer-name"
                    autoFocus
                    className="mt-1 w-full border border-ink/15 bg-white px-3 py-2.5 rounded-md font-sans text-ink text-[15px] focus:outline-none focus:ring-1 focus:ring-ink"
                    placeholder="Jane Smith"
                  />
                </label>
                <label className="block">
                  <span className="font-sans text-[12px] uppercase tracking-[0.18em] text-ink/60">Email</span>
                  <input
                    type="email"
                    value={buyerEmail}
                    onChange={(e) => setBuyerEmail(e.target.value)}
                    data-testid="buyer-email"
                    className="mt-1 w-full border border-ink/15 bg-white px-3 py-2.5 rounded-md font-sans text-ink text-[15px] focus:outline-none focus:ring-1 focus:ring-ink"
                    placeholder="jane@example.com"
                  />
                </label>
              </div>
              <div className="mt-6 p-4 rounded-lg bg-ink/[0.04] border border-ink/10 flex items-start gap-3" data-testid="hold-warning">
                <Clock size={16} className="text-ink/60 shrink-0 mt-0.5"/>
                <div className="font-sans text-[13px] text-ink/80 leading-relaxed">
                  <strong className="text-ink">Your seats are held for {trip?.hold_minutes || 10} minutes</strong> once you continue. Your reservation is only confirmed after PayPal captures your payment — until then, other travellers may book these seats.
                </div>
              </div>
              <label className="mt-4 flex items-start gap-2.5 cursor-pointer" data-testid="tos-accept">
                <input
                  type="checkbox"
                  checked={tosAccepted}
                  onChange={(e) => setTosAccepted(e.target.checked)}
                  className="mt-1 accent-ink"
                />
                <span className="font-sans text-[13px] text-ink/75 leading-relaxed">
                  I accept the <a href="/tos" target="_blank" rel="noopener noreferrer" className="underline hover:text-ink">terms &amp; conditions</a>, including the cancellation policy and the 10-minute seat hold.
                </span>
              </label>
              {error && (
                <div className="mt-4 p-3 rounded-md bg-red-50 border border-red-200 text-[13px] text-red-800 font-sans" data-testid="modal-error">
                  {error}
                </div>
              )}
              <div className="mt-6 pt-4 border-t border-ink/10 flex items-baseline justify-between font-sans">
                <div className="text-ink/60 text-[12px] uppercase tracking-wider">Total due today</div>
                <div className="font-serif text-3xl text-ink tabular">{fmt(total)}</div>
              </div>
            </div>
          )}
        </div>

        {/* Footer nav */}
        <div className="px-6 sm:px-10 py-5 bg-white border-t border-ink/10 rounded-b-xl flex items-center justify-between gap-3">
          <button
            onClick={step === 1 ? onClose : () => setStep(step - 1)}
            data-testid="modal-back"
            className="font-sans text-[14px] text-ink/60 hover:text-ink px-2 py-2"
          >
            {step === 1 ? "Cancel" : "← Back"}
          </button>
          {step < 3 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={(step === 1 && !canNext1) || (step === 2 && !canNext2)}
              data-testid="modal-next"
              className="inline-flex items-center gap-2 bg-ink hover:bg-black disabled:bg-ink/25 disabled:cursor-not-allowed text-white px-5 py-2.5 rounded-full text-sm font-sans font-medium transition-colors"
            >
              Continue <ArrowRight size={14}/>
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              data-testid="modal-submit"
              className="inline-flex items-center gap-2 bg-ink hover:bg-black disabled:bg-ink/25 disabled:cursor-not-allowed text-white px-5 py-2.5 rounded-full text-sm font-sans font-medium transition-colors"
            >
              {submitting ? (
                <><Loader2 size={14} className="animate-spin"/> Reserving…</>
              ) : (
                <>Continue to payment <ArrowRight size={14}/></>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


/* Shared brand tokens for the Fora aesthetic — kicker labels, cream bg,
   ink text. Named export so PublicCuratedPayment (and any future
   curated-flow page) can render the same theme without duplicating
   the CSS. Without this, entering /pay/curated/{token} directly
   (which is how buyers arrive from email) leaves bg-ink undefined
   → white "Pay with PayPal" text on transparent background → the
   whole CTA becomes invisible on the cream page background. */
export function ForaStyles() {
  return (
    <style>{`
      .bg-cream { background-color: #f5f1ea; }
      .text-ink { color: #12100e; }
      .border-ink\\/10 { border-color: rgba(18,16,14,0.10); }
      .border-ink\\/20 { border-color: rgba(18,16,14,0.20); }
      .text-ink\\/40 { color: rgba(18,16,14,0.40); }
      .text-ink\\/50 { color: rgba(18,16,14,0.50); }
      .text-ink\\/60 { color: rgba(18,16,14,0.60); }
      .text-ink\\/70 { color: rgba(18,16,14,0.70); }
      .text-ink\\/80 { color: rgba(18,16,14,0.80); }
      .text-ink\\/85 { color: rgba(18,16,14,0.85); }
      .bg-ink { background-color: #12100e; }
      .kicker {
        font-family: Raleway, system-ui, sans-serif;
        font-size: 10px;
        letter-spacing: 0.25em;
        text-transform: uppercase;
        color: #B08749;
      }
      /* Editorial fallbacks used when a Travefy item arrives without any
         photo — the layout leans on paper, rules and gold accents instead
         of blank space or mismatched stock imagery. */
      .text-gold { color: #B08749; }
      .bg-gold-light { background-color: #f5efe6; }
      .hairline-gold {
        display: block;
        height: 1px;
        background-color: rgba(176,135,73,0.35);
      }
      .paper-callout {
        background-color: #fbf8f3;
        border: 1px solid rgba(176,135,73,0.28);
        border-left: 2px solid rgba(176,135,73,0.55);
      }
      /* Trip view uses the serif for display, Raleway for body */
      body { font-family: Raleway, Manrope, system-ui, sans-serif; }
      .font-serif { font-family: 'Cormorant Garamond', Georgia, serif; font-weight: 500; }
      .font-sans { font-family: Raleway, system-ui, sans-serif; }
    `}</style>
  );
}
